import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import { useStore } from "@Store/";

import Container from "@components/Container";
import RichText from "@components/RichText";
import Spacer from "@components/Spacer";

import {
  HeroBackground,
  HeroContent,
  HeroLink,
  HeroList,
  HeroListItem,
  HeroMain,
  HeroOverlay,
  HeroTitle,
  HeroButton,
  HeroVideo,
} from "./index.style";

gsap.registerPlugin(ScrollTrigger);

const HASH_SCROLL_DELAY = 500;
const SCROLL_LOCK_DURATION = 300;
const SCROLL_FALLBACK_OFFSET = 60;

const getAssetUrl = (asset) => {
  const url = asset?.file?.url;

  if (!url) return null;

  return url.startsWith("//") ? `https:${url}` : url;
};

const Hero = ({ video, image, title, list }) => {
  const [store] = useStore();

  const titleRef = useRef(null);
  const listRef = useRef(null);
  const sectionRef = useRef(null);

  const smoothScrollRef = useRef(store?.smoothScroll);
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef(null);
  const hashScrollTimeoutRef = useRef(null);

  const videoUrl = getAssetUrl(video);
  const imageUrl = getAssetUrl(image);
  const videoType =
    video?.file?.contentType || "video/mp4";

  smoothScrollRef.current = store?.smoothScroll;

  const refresh = () => {
    const smoothScroll = smoothScrollRef.current;

    if (
      smoothScroll &&
      typeof smoothScroll.refresh === "function"
    ) {
      smoothScroll.refresh();
    } else {
      ScrollTrigger.refresh();
    }
  };

  const syncScrollTriggers = () => {
    requestAnimationFrame(() => {
      ScrollTrigger.update();

      requestAnimationFrame(() => {
        ScrollTrigger.update();

        isScrollingRef.current = false;
      });
    });
  };

  const scrollToElement = (element) => {
    if (!element || isScrollingRef.current) return;

    const smoothScroll = smoothScrollRef.current;

    isScrollingRef.current = true;

    if (scrollTimeoutRef.current) {
      window.clearTimeout(
        scrollTimeoutRef.current,
      );
    }

    /*
     * Do not call ScrollTrigger.refresh() immediately before
     * or during the jump. Refreshing while pinned sections are
     * being repositioned can leave ScrollSmoother and
     * ScrollTrigger out of sync.
     */
    if (
      smoothScroll &&
      typeof smoothScroll.scrollTo === "function"
    ) {
      try {
        /*
         * GSAP ScrollSmoother-compatible signature:
         * scrollTo(target, smooth, position)
         */
        smoothScroll.scrollTo(
          element,
          false,
          "top top",
        );
      } catch (error) {
        const targetTop =
          element.getBoundingClientRect().top +
          window.scrollY -
          SCROLL_FALLBACK_OFFSET;

        window.scrollTo(0, targetTop);
      }
    } else {
      const targetTop =
        element.getBoundingClientRect().top +
        window.scrollY -
        SCROLL_FALLBACK_OFFSET;

      window.scrollTo(0, targetTop);
    }

    /*
     * Update trigger progress after the programmatic jump
     * without rebuilding pin spacers.
     */
    syncScrollTriggers();

    /*
     * Safety fallback in case the custom scroller updates
     * asynchronously.
     */
    scrollTimeoutRef.current =
      window.setTimeout(() => {
        ScrollTrigger.update();
        isScrollingRef.current = false;
      }, SCROLL_LOCK_DURATION);
  };

  const scrollToTargetId = (targetRef) => {
    if (
      !targetRef ||
      typeof document === "undefined"
    ) {
      return;
    }

    const targetId = decodeURIComponent(
      targetRef.replace(/^#/, ""),
    );

    if (!targetId) return;

    const element =
      document.getElementById(targetId);

    if (!element) return;

    scrollToElement(element);
  };

  const scrollTos = (event) => {
    event.preventDefault();

    const targetRef =
      event.currentTarget.getAttribute("data-ref");

    scrollToTargetId(targetRef);
  };

  /*
   * On page load, wait 500ms and check whether the URL
   * contains a hash. When the matching element exists,
   * scroll to it using the same ScrollSmoother-compatible
   * function used by the section buttons.
   */
  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const scrollToUrlHash = () => {
      const { hash } = window.location;

      if (!hash) return;

      hashScrollTimeoutRef.current =
        window.setTimeout(() => {
          scrollToTargetId(hash);
        }, HASH_SCROLL_DELAY);
    };

    if (document.readyState === "complete") {
      scrollToUrlHash();
    } else {
      window.addEventListener(
        "load",
        scrollToUrlHash,
        {
          once: true,
        },
      );
    }

    return () => {
      window.removeEventListener(
        "load",
        scrollToUrlHash,
      );

      if (hashScrollTimeoutRef.current) {
        window.clearTimeout(
          hashScrollTimeoutRef.current,
        );
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const section = sectionRef.current;

    if (!section) return undefined;

    let refreshFrameId;

    const refreshAfterLayout = () => {
      if (refreshFrameId) {
        cancelAnimationFrame(refreshFrameId);
      }

      refreshFrameId = requestAnimationFrame(
        refresh,
      );
    };

    const resize = () => {
      gsap.matchMediaRefresh();
      refreshAfterLayout();
    };

    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();

      mm.add("(min-width:1024px)", () => {
        const items = listRef.current
          ? Array.from(
              listRef.current.querySelectorAll(
                "li",
              ),
            )
          : [];

        const timeline = gsap.timeline({
          defaults: {
            ease: "power3.out",
          },
        });

        if (titleRef.current) {
          timeline.fromTo(
            titleRef.current,
            {
              y: 60,
              autoAlpha: 0,
            },
            {
              y: 0,
              autoAlpha: 1,
              duration: 1,
              clearProps:
                "transform,opacity,visibility",
            },
          );
        }

        if (items.length > 0) {
          timeline.fromTo(
            items,
            {
              y: 40,
              autoAlpha: 0,
            },
            {
              y: 0,
              autoAlpha: 1,
              duration: 0.7,
              stagger: 0.12,
              clearProps:
                "transform,opacity,visibility",
            },
            "-=0.5",
          );
        }

        refreshAfterLayout();

        return () => {
          timeline.kill();
        };
      });

      mm.add("(max-width:1023px)", () => {
        const items = listRef.current
          ? Array.from(
              listRef.current.querySelectorAll(
                "li",
              ),
            )
          : [];

        const elements = [
          titleRef.current,
          ...items,
        ].filter(Boolean);

        if (elements.length > 0) {
          gsap.set(elements, {
            clearProps: "all",
          });
        }
      });

      refreshAfterLayout();

      return () => {
        mm.revert();
      };
    }, section);

    window.addEventListener(
      "load",
      refreshAfterLayout,
    );

    window.addEventListener("resize", resize);

    return () => {
      if (refreshFrameId) {
        cancelAnimationFrame(refreshFrameId);
      }

      ctx.revert();

      window.removeEventListener(
        "load",
        refreshAfterLayout,
      );

      window.removeEventListener(
        "resize",
        resize,
      );

      if (scrollTimeoutRef.current) {
        window.clearTimeout(
          scrollTimeoutRef.current,
        );
      }

      isScrollingRef.current = false;
    };
  }, [list, store?.smoothScroll]);

  return (
    <HeroMain
      ref={sectionRef}
      aria-labelledby="hero-title"
    >
      <HeroBackground>
        {videoUrl && (
          <HeroVideo
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster={imageUrl || undefined}
            aria-hidden="true"
          >
            <source
              src={videoUrl}
              type={videoType}
            />
          </HeroVideo>
        )}

        {!videoUrl && imageUrl && (
          <img
            src={imageUrl}
            alt=""
            aria-hidden="true"
            fetchPriority="high"
          />
        )}

        <HeroOverlay />
      </HeroBackground>

      <Container>
        <HeroContent>
          <Spacer size={120} />

          <HeroTitle
            id="hero-title"
            ref={titleRef}
          >
            <RichText content={title} />
          </HeroTitle>

          {list.length > 0 && (
            <HeroList
              ref={listRef}
              aria-label="Page sections"
            >
              {list.map((item, index) => {
                const itemUrl = item?.url || "";

                return (
                  <HeroListItem
                    key={
                      item?.id ||
                      item?.contentful_id ||
                      index
                    }
                  >
                    {itemUrl.startsWith("#") ? (
                      <HeroButton
                        type="button"
                        onClick={scrollTos}
                        data-ref={itemUrl}
                      >
                        <RichText
                          content={item?.text}
                        />
                      </HeroButton>
                    ) : (
                      <HeroLink href={itemUrl}>
                        <RichText
                          content={item?.text}
                        />
                      </HeroLink>
                    )}
                  </HeroListItem>
                );
              })}
            </HeroList>
          )}
        </HeroContent>
      </Container>
    </HeroMain>
  );
};

Hero.propTypes = {
  video: PropTypes.object,
  image: PropTypes.object,
  title: PropTypes.object,
  list: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      contentful_id: PropTypes.string,
      url: PropTypes.string,
      text: PropTypes.object,
    }),
  ),
};

Hero.defaultProps = {
  video: null,
  image: null,
  title: null,
  list: [],
};

export default Hero;